import React, { useState, useEffect } from "react";
import { toast } from "@/components/ui/use-toast";
import { AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle, AlertDialogDescription, AlertDialogFooter, AlertDialogAction } from "@/components/ui/alert-dialog";
import { SentimentAnalysisForm } from "./sentiment/SentimentAnalysisForm";
import { SentimentLoadingState } from "./sentiment/SentimentLoadingState";
import { OverallSentimentChart } from "./sentiment/OverallSentimentChart";
import { KeyInsights } from "./sentiment/KeyInsights";
import { SentimentCharts } from "./sentiment/SentimentCharts";
import { SentimentErrorDialog } from "./sentiment/SentimentErrorDialog";
import { 
  analyzeSentiment, 
  getFallbackResults, 
  SentimentResults 
} from "./sentiment/sentimentService";

const SENTIMENT_QUESTIONS = [
  "What do people think about our product or service quality?", 
  "How is our customer service perceived?", 
  "What's the sentiment around our recent announcements?", 
  "How does our brand compare to competitors?", 
  "What features do customers want to see improved?"
];

export const SentimentAnalysisTab = () => {
  const [companyName, setCompanyName] = useState("");
  const [selectedQuestion, setSelectedQuestion] = useState(SENTIMENT_QUESTIONS[0]);
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [results, setResults] = useState<SentimentResults | null>(null);
  const [aiEngine, setAiEngine] = useState('openai');
  const [progressValue, setProgressValue] = useState(0);
  const [isErrorDialogOpen, setIsErrorDialogOpen] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [showNetworkAlert, setShowNetworkAlert] = useState(false);
  const [keyInsights, setKeyInsights] = useState<{
    positiveTrends: string,
    areasForImprovement: string,
    platformFocus: string
  }>({
    positiveTrends: "",
    areasForImprovement: "",
    platformFocus: ""
  });
  
  // Add effect to handle progress animation during analysis
  useEffect(() => {
    let progressInterval: ReturnType<typeof setInterval> | null = null;
    
    if (isAnalyzing) {
      setProgressValue(0);
      progressInterval = setInterval(() => {
        setProgressValue(prev => {
          // Increase progressively but cap at 95% to avoid appearing complete
          // before actual results arrive
          if (prev >= 95) {
            clearInterval(progressInterval!);
            return 95;
          }
          // Increase faster at first, then slower
          const increment = prev < 30 ? 5 : prev < 60 ? 3 : 1;
          return prev + increment;
        });
      }, 300);
    } else {
      // When analysis is done, fill to 100%
      setProgressValue(100);
    }
    
    return () => {
      if (progressInterval) clearInterval(progressInterval);
    };
  }, [isAnalyzing]);
  
  const handleAnalyzeSentiment = async () => {
    if (!companyName) {
      toast({
        title: "Company name required",
        description: "Please enter a company name to analyze sentiment.",
        variant: "destructive"
      });
      return;
    }
    
    setIsAnalyzing(true);
    setResults(null);
    setKeyInsights({
      positiveTrends: "",
      areasForImprovement: "",
      platformFocus: ""
    });
    
    try {
      const analysisResults = await analyzeSentiment(companyName, selectedQuestion, aiEngine);
      
      if (!analysisResults || !analysisResults.overall) {
        setShowNetworkAlert(true);
        return;
      }
      
      setResults(analysisResults);
      
      if (analysisResults.insights) {
        setKeyInsights({
          positiveTrends: analysisResults.insights.positiveTrends || "",
          areasForImprovement: analysisResults.insights.areasForImprovement || "",
          platformFocus: analysisResults.insights.platformFocus || ""
        });
      }
      
      toast({
        title: "Analysis complete",
        description: `Sentiment analysis for ${companyName} completed successfully`,
      });
    } catch (error) {
      console.error("Error analyzing sentiment:", error);
      setShowNetworkAlert(true);
    } finally {
      setIsAnalyzing(false);
    }
  };
  
  return (
    <div className="space-y-6 text-white">
      <SentimentErrorDialog
        isOpen={isErrorDialogOpen}
        onClose={() => setIsErrorDialogOpen(false)}
        errorMessage={errorMessage}
      />

      <SentimentAnalysisForm
        companyName={companyName}
        setCompanyName={setCompanyName}
        selectedQuestion={selectedQuestion}
        setSelectedQuestion={setSelectedQuestion}
        aiEngine={aiEngine}
        setAiEngine={setAiEngine}
        onAnalyze={handleAnalyzeSentiment}
        isAnalyzing={isAnalyzing}
      />

      {isAnalyzing && (
        <SentimentLoadingState progressValue={progressValue} />
      )}

      {results && (
        <div className="space-y-6">
          {/* First row with Overall Sentiment and Key Insights side by side */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <OverallSentimentChart
              overall={results.overall}
              categories={results.categories}
            />
            
            <KeyInsights insights={keyInsights} />
          </div>

          {/* Second row with the three charts in equal width */}
          <SentimentCharts results={results} />
        </div>
      )}

      <AlertDialog open={showNetworkAlert} onOpenChange={setShowNetworkAlert}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Network Issue</AlertDialogTitle>
            <AlertDialogDescription>
              Network conditions are affecting the query. Please try again, or try using a different AI engine.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogAction onClick={() => setShowNetworkAlert(false)}>OK</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};
